import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../../redux";
import { setExpandedItem } from "../../redux/modules/expanders";
import { FontIcon, mergeStyles } from "@fluentui/react";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import SubTitle from "./SubTitle";
import { detectIEOrEdge, IEEdgeSmoothScroll } from "../../../../utilities/BrowserUtils";
import { ExpanderStateEnum } from "../enums/expanderStateEnum";
import { mobileListItemStyle, isMobile } from "../../styles/mobileStyles"

const mapStateToProps = (state: RootState) => {
  return {
    expanders: state.expanders
  };
};
const mapDispatchToProps = { setExpandedItem };

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
interface SubTitleProps extends PropsFromRedux {
  title: string;
  // iconName?: string;
  iconElement?: JSX.Element;
  expanderNameToToggle?: string;
  items: JSX.Element[];
  itemsSelected?: number;
  extra?: JSX.Element;
  hasShowMoreButton?: boolean;
  expanderState?: ExpanderStateEnum;
  fixedHeight?: number;
  isWaitingOnContent?: boolean;
  selectedItemsMoreClicked?: () => void;
  showMoreClicked?: (showMoreIsClicked: boolean) => void;
  filterButton?: JSX.Element;
  clearFilterButton?: JSX.Element;
  sortOptions?: JSX.Element;
  actionButton?: JSX.Element;
  canToggle?: boolean;
  allowScroll?: boolean;
}

const minItems = 5;
const subTitleHeight = 34;

const ExpanderWithSubtitle: React.FunctionComponent<SubTitleProps> = ({
  title,
  // iconName,
  iconElement,
  expanders,
  setExpandedItem,
  expanderNameToToggle,
  items,
  itemsSelected,
  extra,
  hasShowMoreButton,
  expanderState,
  fixedHeight,
  isWaitingOnContent,
  selectedItemsMoreClicked,
  showMoreClicked,
  filterButton,
  clearFilterButton,
  sortOptions,
  actionButton,
  canToggle,
  allowScroll
}: SubTitleProps) => {
  const languageStrings = React.useContext(LanguageContext);
  const [showMoreIsClicked, setShowMore] = React.useState<boolean>(false);
  const [scrollPosition, setScrollPosition] = React.useState<number>(0);

  const collapsed = expanders[expanderNameToToggle];
  React.useEffect(() => {
    if ((expanderState === ExpanderStateEnum.expanded && collapsed) || (expanderState === ExpanderStateEnum.collapsed && !collapsed)) {
      setExpandedItem({ toggle: !expanders[expanderNameToToggle], property: expanderNameToToggle });
    }
  }, [expanderState]);

  const calculateItemsToDisplay = () => {
    const itemHeight = isMobile ? mobileListItemStyle.height : 40;
    return fixedHeight
      ? fixedHeight + subTitleHeight + (extra ? itemHeight : 0)
      : showMoreIsClicked || !hasShowMoreButton
        ? itemHeight * items.length + (extra ? itemHeight : 0) + subTitleHeight
        : itemHeight * Math.min(items.length, minItems) + subTitleHeight;
  };

  const sectionStyle = allowScroll
    ? mergeStyles({
      maxHeight: collapsed ? subTitleHeight : calculateItemsToDisplay(),
      // overflow: "hidden",
      // transition: "max-height 0.5s ease-in-out"
      overflowY: "hidden",
      width: "max-content",
      minWidth: "100%"
    })
    : mergeStyles({
      maxHeight: collapsed ? subTitleHeight : calculateItemsToDisplay(),
      overflow: "hidden"
    });

  const showMoreStyle = {
    maxHeight: collapsed ? 0 : 16,
    // transition: "all 0.35s ease-in-out 0.1s",
    overflow: "hidden",
    paddingTop: collapsed ? 0 : 5,
    paddingBottom: collapsed ? 0 : 5
  };

  const toggleShowMore = () => {
    if (showMoreClicked) {
      showMoreClicked(!showMoreIsClicked);
    }

    let scrollableContainer = document.querySelector(".xb-content-container");
    if (!showMoreIsClicked) {
      //About to expand
      setScrollPosition(scrollableContainer.scrollTop);
    } else {
      if (detectIEOrEdge()) {
        // scrollableContainer.scrollTop = scrollPosition;
        IEEdgeSmoothScroll(scrollableContainer, scrollPosition, 500);
      } else {
        let scrollOptions: ScrollToOptions = {
          top: scrollPosition,
          left: 0,
          behavior: "smooth"
        };
        scrollableContainer.scrollTo(scrollOptions);
      }
    }

    setShowMore(prevShowMoreIsClicked => !prevShowMoreIsClicked);
  };

  const showMore = items.length > minItems && hasShowMoreButton && (
    <div className="xb-show-more-action" style={showMoreStyle} onClick={() => toggleShowMore()}>
      <FontIcon iconName={!showMoreIsClicked ? "ChevronDown" : "ChevronUp"} />
      <span>{!showMoreIsClicked ? languageStrings.ShowMore : languageStrings.ShowLess}</span>
    </div>
  );

  return (
    <>
      <div className={sectionStyle}>
        <SubTitle
          title={title}
          // iconName={iconName}
          iconElement={iconElement}
          itemsSelected={itemsSelected}
          expanderNameToToggle={expanderNameToToggle}
          isWaitingOnContent={isWaitingOnContent}
          selectedItemsMoreClicked={selectedItemsMoreClicked}
          filterButton={filterButton}
          clearFilterButton={clearFilterButton}
          sortOptions={sortOptions}
          actionButton={actionButton}
          canToggle={canToggle}
        />
        {!collapsed && ((fixedHeight || (!hasShowMoreButton || showMoreIsClicked)) ? items : items.slice(0, minItems))}
        {!collapsed && extra}
      </div>
      {!collapsed && showMore}
    </>
  );
};
export default connector(ExpanderWithSubtitle);
